
import React, { createContext, useContext, useState, useEffect } from 'react';
import { Product } from '@/types/product';
import { useLocation, useParams } from 'react-router-dom';
import { FEATURED_PRODUCTS } from '@/data/products';

interface ProductDetailContextType {
  isOpen: boolean;
  currentProduct: Product | null;
  openProductDetail: (product: Product) => void;
  closeProductDetail: () => void;
}

const ProductDetailContext = createContext<ProductDetailContextType | undefined>(undefined);

export function ProductDetailProvider({ children }: { children: React.ReactNode }) {
  const [isOpen, setIsOpen] = useState(false);
  const [currentProduct, setCurrentProduct] = useState<Product | null>(null);
  const location = useLocation();
  const { id } = useParams();

  // Auto-open product detail sheet when navigating directly to a product URL
  useEffect(() => {
    if (id) {
      const product = FEATURED_PRODUCTS.find(p => p.id === id || p.title.toLowerCase().replace(/\s+/g, '-') === id);
      if (product) {
        setCurrentProduct(product);
        setIsOpen(true);
      }
    }
  }, [id]);

  const openProductDetail = (product: Product) => {
    setCurrentProduct(product);
    setIsOpen(true);
  };

  const closeProductDetail = () => {
    setIsOpen(false);
  };

  return (
    <ProductDetailContext.Provider
      value={{ isOpen, currentProduct, openProductDetail, closeProductDetail }}
    >
      {children}
    </ProductDetailContext.Provider>
  );
}

export function useProductDetail() {
  const context = useContext(ProductDetailContext);
  if (context === undefined) {
    throw new Error('useProductDetail must be used within a ProductDetailProvider');
  }
  return context;
}
